import { IStripePaymentMethod } from '../interfaces/models/user'

export const CARD_EXPIRING_SOON_DAYS = 15

export type CardExpiryBannerState = {
  variant: 'expired' | 'expiring_soon'
  daysRemaining: number | null
  last4?: string
}

/** Last moment of the expiry month (cards are valid through end of expiry month). */
function getLastValidInstant(expMonth: number, expYear: number): Date {
  return new Date(expYear, expMonth, 0, 23, 59, 59, 999)
}

function getExpiryStatus(expMonth: number, expYear: number): {
  status: 'expired' | 'expiring_soon' | 'ok'
  daysRemaining: number
} {
  const today = new Date()
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const lastValid = getLastValidInstant(expMonth, expYear)
  const lastValidStart = new Date(lastValid.getFullYear(), lastValid.getMonth(), lastValid.getDate())

  const msPerDay = 24 * 60 * 60 * 1000
  const daysRemaining = Math.round((lastValidStart.getTime() - todayStart.getTime()) / msPerDay)

  if (daysRemaining < 0) return { status: 'expired', daysRemaining: 0 }
  if (daysRemaining <= CARD_EXPIRING_SOON_DAYS) return { status: 'expiring_soon', daysRemaining }
  return { status: 'ok', daysRemaining }
}

/** True when the card is past its expiry month (needs expMonth/expYear from API). */
export function isPaymentMethodExpired(pm: IStripePaymentMethod): boolean {
  if (
    pm.expMonth == null ||
    pm.expYear == null ||
    typeof pm.expMonth !== 'number' ||
    typeof pm.expYear !== 'number'
  ) {
    return false
  }
  return getExpiryStatus(pm.expMonth, pm.expYear).status === 'expired'
}

/**
 * Check consistency between two sources on `/me` (and similar responses):
 *
 * - **`paymentIntentPaymentMethod`** — comes from **subscription** document: Stripe PM id bound to the last PaymentIntent / subscription charge.
 * - **`paymentMethods`** — list of cards typically from **workspace** document (combined in the response), with `isDefault` and `expMonth` / `expYear`.
 *
 * For reliable behavior (e.g. card expiry banner) we want the card used by Stripe for subscription to be the same as **default** in the list:
 * there is an item in `paymentMethods` whose `id` equals `paymentIntentPaymentMethod` and **`isDefault` is true**.
 *
 * If there is no `paymentIntentPaymentMethod`, no list, or id is not in the list / not default — returns **`false`** (not considered aligned).
 */
export function isSubscriptionPaymentMethodAlignedWithDefault(
  subscription?: {
    paymentIntentPaymentMethod?: string | null
    paymentMethods?: IStripePaymentMethod[]
  } | null
): boolean {
  const intentPmId = subscription?.paymentIntentPaymentMethod
  const methods = subscription?.paymentMethods
  if (!intentPmId || !methods?.length) return false

  const matched = methods.find((m) => m.id === intentPmId)
  return matched?.isDefault === true
}

/**
 * Picks the best available payment-method list for UI that mirrors the billing settings table.
 * Starts from `subscription.paymentMethods`; if the workspace copy has *more* entries (e.g. a new card
 * was added during enrichment before subscription was refreshed), use `workspace.stripePaymentMethods` instead.
 */
export function mergePaymentMethodsForBanner(
  subscription?: { paymentMethods?: IStripePaymentMethod[] },
  workspace?: { stripePaymentMethods?: IStripePaymentMethod[] }
): IStripePaymentMethod[] {
  let paymentMethods: IStripePaymentMethod[] = []
  if (subscription?.paymentMethods?.length) paymentMethods = subscription.paymentMethods
  if ((workspace?.stripePaymentMethods?.length ?? 0) > (subscription?.paymentMethods?.length ?? 0)) {
    paymentMethods = workspace?.stripePaymentMethods ?? []
  }
  return paymentMethods
}

/**
 * Only the default payment method is used for subscription charges — ignore other cards on file.
 */
export function getCardExpiryBannerState(
  methods: IStripePaymentMethod[]
): CardExpiryBannerState | null {
  const defaultPm = methods.find((m) => m.isDefault)
  if (!defaultPm) return null

  const hasExpiry =
    defaultPm.expMonth != null &&
    defaultPm.expYear != null &&
    typeof defaultPm.expMonth === 'number' &&
    typeof defaultPm.expYear === 'number'
  if (!hasExpiry) return null

  const { status, daysRemaining } = getExpiryStatus(defaultPm.expMonth!, defaultPm.expYear!)

  if (status === 'expired') {
    return { variant: 'expired', daysRemaining: null, last4: defaultPm.last4 }
  }
  if (status === 'expiring_soon') {
    return {
      variant: 'expiring_soon',
      daysRemaining,
      last4: defaultPm.last4,
    }
  }

  return null
}
